import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';

const CouponList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Filter state for each column
  const [filters, setFilters] = useState({
    transactionDate: '',
    companyName: '',
    country: '',
    coupons: '',
    unitPrice: '',
    totalPrice: '',
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCoupons(selectedCompany);
    } else {
      setCoupons([]);
      setFilteredCoupons([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    applyFilters();
  }, [coupons, filters]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/Companies/active-agencies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies. Please try again.');
    }
  };

  const fetchCoupons = async (companyId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/Coupons?companyId=${companyId}`);
      const data = response.data;
      setCoupons(data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setError('Failed to load coupons. Please try again.');
      setCoupons([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...coupons];

    // Filter by transaction date - starts with
    if (filters.transactionDate) {
      const searchDate = filters.transactionDate.toLowerCase();
      filtered = filtered.filter((coupon) => {
        const date = formatDate(coupon.transactionDate).toLowerCase();
        return date.startsWith(searchDate);
      });
    }

    // Filter by company name - starts with
    if (filters.companyName) {
      const search = filters.companyName.toLowerCase();
      filtered = filtered.filter((coupon) => {
        const companyName = getCompanyName(coupon.companyID).toLowerCase();
        return companyName.startsWith(search);
      });
    }

    // Filter by country - starts with
    if (filters.country) {
      const search = filters.country.toLowerCase();
      filtered = filtered.filter((coupon) => {
        const country = getCompanyCountry(coupon.companyID).toLowerCase();
        return country.startsWith(search);
      });
    }

    // Filter by coupons - starts with
    if (filters.coupons) {
      const search = filters.coupons.toLowerCase();
      filtered = filtered.filter((coupon) => {
        const couponsStr = formatNumber(coupon.coupons).toLowerCase();
        return couponsStr.startsWith(search);
      });
    }

    // Filter by unit price - starts with
    if (filters.unitPrice) {
      const search = filters.unitPrice.toLowerCase();
      filtered = filtered.filter((coupon) => {
        const priceStr = formatCurrency(coupon.unitPrice).toLowerCase();
        return priceStr.startsWith(search);
      });
    }

    // Filter by total price - starts with
    if (filters.totalPrice) {
      const search = filters.totalPrice.toLowerCase();
      filtered = filtered.filter((coupon) => {
        const priceStr = formatCurrency(coupon.totalPrice).toLowerCase();
        return priceStr.startsWith(search);
      });
    }

    setFilteredCoupons(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
    setPage(0);
    // Reset filters when company changes
    setFilters({
      transactionDate: '',
      companyName: '',
      country: '',
      coupons: '',
      unitPrice: '',
      totalPrice: '',
    });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    navigate('/coupons/create', { state: { companyId: selectedCompany } });
  };

  const handleEdit = (id) => {
    navigate(`/coupons/edit/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await api.delete(`/Coupons/${id}`);
        if (selectedCompany) {
          fetchCoupons(selectedCompany);
        }
      } catch (error) {
        console.error('Error deleting coupon:', error);
        setError('Failed to delete coupon. Please try again.');
      }
    }
  };

  const handleRefresh = () => {
    if (selectedCompany) {
      fetchCoupons(selectedCompany);
      // Reset filters
      setFilters({
        transactionDate: '',
        companyName: '',
        country: '',
        coupons: '',
        unitPrice: '',
        totalPrice: '',
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.companyID === companyId);
    return company ? company.companyName : 'N/A';
  };

  const getCompanyCountry = (companyId) => {
    const company = companies.find(c => c.companyID === companyId);
    return company ? company.country : 'N/A';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };

  // Pagination
  const paginatedCoupons = filteredCoupons.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Render filter row
  const renderFilterRow = () => (
    <TableRow sx={{ bgcolor: '#fafafa' }}>
      <TableCell sx={{ p: 1, width: '100px' }}>
        <TextField
          size="small"
          placeholder="Filter date"
          value={filters.transactionDate}
          onChange={handleFilterChange('transactionDate')}
          fullWidth
          variant="outlined"
          disabled={!selectedCompany}
        />
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Filter company"
          value={filters.companyName}
          onChange={handleFilterChange('companyName')}
          fullWidth
          variant="outlined"
          disabled={!selectedCompany}
        />
      </TableCell>
      <TableCell sx={{ p: 1, width: '100px' }}>
        <TextField
          size="small"
          placeholder="Filter country"
          value={filters.country}
          onChange={handleFilterChange('country')}
          fullWidth
          variant="outlined"
          disabled={!selectedCompany}
        />
      </TableCell>
      <TableCell sx={{ p: 1, width: '85px' }}>
        <TextField
          size="small"
          placeholder="Filter coupons"
          value={filters.coupons}
          onChange={handleFilterChange('coupons')}
          fullWidth
          variant="outlined"
          disabled={!selectedCompany}
        />
      </TableCell>
      <TableCell sx={{ p: 1, width: '85px' }}>
        <TextField
          size="small"
          placeholder="Filter price"
          value={filters.unitPrice}
          onChange={handleFilterChange('unitPrice')}
          fullWidth
          variant="outlined"
          disabled={!selectedCompany}
        />
      </TableCell>
      <TableCell sx={{ p: 1, maxWidth: '150px' }}>
        <TextField
          size="small"
          placeholder="Filter total"
          value={filters.totalPrice}
          onChange={handleFilterChange('totalPrice')}
          fullWidth
          variant="outlined"
          disabled={!selectedCompany}
        />
      </TableCell>
      <TableCell sx={{ p: 1 }} align="center">
        <Tooltip title="Clear Filters">
          <IconButton
            size="small"
            onClick={() => setFilters({
              transactionDate: '',
              companyName: '',
              country: '',
              coupons: '',
              unitPrice: '',
              totalPrice: '',
            })}
            disabled={!selectedCompany}
          >
            ✕
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3, maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Coupons
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton onClick={handlePrint} color="primary">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              disabled={!selectedCompany}
              sx={{ ml: 1 }}
            >
              New Coupon
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Company Dropdown */}
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Select Company</InputLabel>
                  <Select
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    label="Select Company"
                  >
                    <MenuItem value="">
                      <em>Select a company</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.companyID} value={company.companyID}>
                        {company.companyName} - {company.country || 'N/A'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Table */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!selectedCompany ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <Typography variant="h6" color="textSecondary">
                  Please select a company to view coupons
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>Country</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '85px' }} align="right">Coupons</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: '85px' }} align="right">Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', maxWidth: '150px' }} align="right">Total Price</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                      </TableRow>
                      {/* Filter Row */}
                      {renderFilterRow()}
                    </TableHead>
                    <TableBody>
                      {paginatedCoupons.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                              No coupons found for this company
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedCoupons.map((coupon) => (
                          <TableRow key={coupon.id} hover>
                            <TableCell sx={{ width: '100px' }}>{formatDate(coupon.transactionDate)}</TableCell>
                            <TableCell>{getCompanyName(coupon.companyID)}</TableCell>
                            <TableCell sx={{ width: '100px' }}>
                              <Chip 
                                label={getCompanyCountry(coupon.companyID)} 
                                size="small" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ width: '85px' }} align="right">{formatNumber(coupon.coupons)}</TableCell>
                            <TableCell sx={{ width: '100px' }} align="right">{formatCurrency(coupon.unitPrice)}</TableCell>
                            <TableCell sx={{ maxWidth: '150px' }} align="right">
                              <Typography variant="body1" fontWeight="bold" color="primary" noWrap>
                                {formatCurrency(coupon.totalPrice)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEdit(coupon.id)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              {/* Delete  
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(coupon.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>*/}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredCoupons.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CouponList;